import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, Toggle } from "@/components/primitives";

const OWN_OPTS = [["rented", "Rented"], ["financed", "Financed"], ["sold", "Owned outright"], ["unknown", "Unknown"]];
const PERIODS = [["monthly", "monthly"], ["quarterly", "quarterly"], ["annually", "annually"]];

function SourceTag({ source }) {
  if (!source) return null;
  const map = { manual: ["Manual", "var(--ac-indigo)"], simpro: ["Simpro", "var(--sv-good)"], derived: ["Derived", "var(--ui-text-lo)"] };
  const [label, color] = map[source] || ["", "var(--ui-text-lo)"];
  return <span className="prose-i" style={{ fontSize: 10, color, border: `1px solid ${color}`,
    borderRadius: 6, padding: "1px 6px", marginLeft: 8 }}>{label}</span>;
}

export default function AccountSettingsPanel({ cid, settings, suggestion, source }) {
  const qc = useQueryClient();
  const s = settings || {};
  const [committed, setCommitted] = useState(s.committedKgPerPeriod ?? "");
  const [period, setPeriod] = useState(s.commitmentPeriod || "monthly");
  const [tol, setTol] = useState(s.tolerancePct ?? 15);
  const [ownership, setOwnership] = useState(s.ownershipType || "");
  const [notes, setNotes] = useState(s.notes || "");
  const [saved, setSaved] = useState(false);

  const commSource = s?.source?.committedKg;
  const ownSource = s?.source?.ownership;

  const save = async () => {
    await endpoints.saveAccountSettings(cid, {
      committedKgPerPeriod: committed === "" ? null : parseFloat(committed),
      commitmentPeriod: period, tolerancePct: parseFloat(tol),
      ownershipType: ownership || null, notes,
    });
    setSaved(true); setTimeout(() => setSaved(false), 1600);
    await qc.invalidateQueries();
  };

  const input = { background: "var(--ui-bg)", border: "1px solid var(--ui-line)", borderRadius: 8,
    padding: "8px 12px", color: "var(--ui-text-hi)", fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, width: 120 };
  const inputProse = { ...input, fontFamily: "'Inter', sans-serif" };
  const row = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderTop: "1px solid var(--ui-line-soft)" };

  return (
    <Card style={{ padding: 24, marginTop: 20 }}>
      <div className="flex items-center" style={{ justifyContent: "space-between" }}>
        <div>
          <div className="num" style={{ fontSize: 17, fontWeight: 500, color: "var(--ui-text-hi)" }}>Account settings</div>
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3 }}>
            Commercial terms held in this app · never written to Simpro
          </div>
        </div>
        <Button data-testid="save-account-settings" onClick={save}>{saved ? "Saved ✓" : "Save"}</Button>
      </div>

      <div style={{ ...row, borderTop: "none" }}>
        <span className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>
          Committed volume <SourceTag source={commSource} />
        </span>
        <div className="flex items-center" style={{ gap: 8 }}>
          <input data-testid="committed-input" type="number" value={committed}
            onChange={(e) => setCommitted(e.target.value)} style={input} placeholder="—" />
          <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>kg /</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} style={{ ...inputProse, width: 110 }}>
            {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>
      {(committed === "" || committed == null) && suggestion != null && (
        <div className="flex items-center" style={{ gap: 10, padding: "2px 0 10px" }}>
          <span className="prose-i" style={{ fontSize: 12, color: "var(--sv-watch)" }}>
            Suggested from history: {suggestion} kg/month
          </span>
          <button data-testid="accept-suggestion" onClick={() => setCommitted(suggestion)} className="prose-i"
            style={{ background: "var(--ui-card-2)", border: "1px solid var(--ui-line)", borderRadius: 8,
              padding: "3px 10px", fontSize: 11, color: "var(--ui-text-mid)", cursor: "pointer" }}>
            Accept
          </button>
        </div>
      )}

      <div style={row}>
        <span className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>Tolerance</span>
        <div className="flex items-center" style={{ gap: 6 }}>
          <input type="number" value={tol} onChange={(e) => setTol(e.target.value)} style={{ ...input, width: 80 }} />
          <span className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)" }}>%</span>
        </div>
      </div>

      <div style={row}>
        <span className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-hi)" }}>
          Ownership type <SourceTag source={ownSource} />
        </span>
        <select data-testid="ownership-select" value={ownership} onChange={(e) => setOwnership(e.target.value)} style={{ ...inputProse, width: 150 }}>
          <option value="">Derived ({source || "unknown"})</option>
          {OWN_OPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      <div style={{ ...row, alignItems: "flex-start" }}>
        <span className="prose-i" style={{ fontSize: 14, color: "var(--ui-text-hi)", paddingTop: 6 }}>Notes</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          style={{ ...inputProse, width: 260, minHeight: 52 }} placeholder="Optional" />
      </div>
    </Card>
  );
}
